import React from 'react';
import Config from 'config';
import { useAppSelector } from '../../../../../hooks/hooks';
import Field from '../../../../../JSApi/constants/Field';
import {
  IApiaCommonComponent,
  IApiaFinder,
  IApiaInput,
  TComponentSubmitValues,
} from '../../../../../types';
import { formatMessage, parseMask } from '../../../../../utils/util';
import MakeField from '../../fieldsStore/MakeField';
import ApiaFinderInput from './ApiaFinderInput';
import ApiaFinderModal from './ApiaFinderModal';
import { TApiaFinderValue } from './types';
import store from '../../../../../store';
import { queryActions } from '../../../../../store/querySlice';
import ApiaFunctions from '../../../../../JSApi/ApiaFunctions';
import useContext from '../../../../../hooks/useContext';
import { ApiaComponentContextPrimitive } from '../../apiaComponent/ApiaComponentContext';
import propsStore from '../../../../../store/propsStore/propsStore';
import usePropsSelector from '../../../../../store/propsStore/usePropsSelector';

const DEFAULT_MASK_PLACEHOLDER = '_';

export function isFinder(field: IApiaCommonComponent): field is IApiaFinder {
  return field.fieldType === Field.TYPE_INPUT && field.qryId !== undefined;
}

export function isFinderValue(
  value: TComponentSubmitValues,
): value is TApiaFinderValue {
  return (
    typeof value === 'object' &&
    value &&
    'storeValue' in value &&
    'showValue' in value
  );
}

const ApiaFinder = MakeField(
  React.forwardRef<HTMLInputElement>((_, ref) => {
    const { fieldId, field } = useContext(ApiaComponentContextPrimitive);
    const props = usePropsSelector(fieldId);

    const { valueType, fieldType, qry_show_value } = field;

    const hasLoadedState = useAppSelector(
      (global) => !!global.querySlice.queries[fieldId]?.columns,
    );

    React.useEffect(() => {
      store.dispatch(
        queryActions.initState({
          fieldId,
          initialState: {
            ajaxUrl: '/apia.query.ModalAction.run',
            queryData: {
              qryId: field.qryId ?? 'NOQUERYID',
              frmId: props.formData?.frmId ?? 'NOFORMPARENT',
              frmParent: props.formData?.frmParent ?? 'NOFLDID',
              fldId: field.id,
              attId: props.attributes?.attId ?? 'NOATTID',
              index: props.attributes?.index ?? 0,
              fieldType,
              valueType,
            },
            inputValue: qry_show_value,
          },
        }),
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.formData?.frmId]);

    return (
      <>
        <ApiaFinderInput ref={ref} />
        {hasLoadedState && <ApiaFinderModal />}
      </>
    );
  }),
  {
    getInitialValue({ context }) {
      const { qry_show_value: initialShowValue, value: initialStoreValue } =
        context.field as IApiaInput;
      if (isFinderValue(initialStoreValue)) return initialStoreValue;
      return {
        showValue: initialShowValue ?? initialStoreValue ?? '',
        storeValue: initialStoreValue ?? '',
      };
    },
    getSubmitValue(value, { field, onGrid }) {
      return !onGrid && (field as IApiaInput).properties.storeMdlQryResult
        ? Object.values({
            storeValue: (value as TApiaFinderValue).storeValue,
            showValue: (value as TApiaFinderValue).showValue,
          })
        : ((value as TApiaFinderValue).storeValue ?? value);
    },
    doValidate({
      fieldContext: {
        componentContext: { fieldId },
        state: { value },
        setError,
      },
    }) {
      const storeValue = isFinderValue(value as TComponentSubmitValues)
        ? (value as TApiaFinderValue).storeValue
        : (value as string);
      if (propsStore.getFieldProps(fieldId).required && !storeValue) {
        setError(formatMessage(window.MSG_REQ_FIELD, { TOK1: '' }));
        return false;
      }
      return true;
    },
    async preSynchronize({
      event,
      fieldContext: {
        componentContext: { field, fieldId },
        isReadonly,
        setError,
        setValue,
        state: { lastValidValue, value },
      },
    }) {
      if (event === 'onModalReturn') {
        const state = store.getState().querySlice.queries[fieldId];

        if (state) {
          const { dispatchRow } = state;
          if (
            typeof dispatchRow === 'object' &&
            dispatchRow &&
            (typeof dispatchRow.id === 'number' ||
              typeof dispatchRow.id === 'string')
          ) {
            const row = state.rows[Number(dispatchRow.id)];

            if (row && row.id) {
              store.dispatch(
                queryActions.setState({
                  loading: state.loading + 1,
                  fieldId,
                  dispatchRow: null,
                }),
              );
              const result = await queryActions.getRowData({
                rowId: row.id,
                ajaxUrl: state.ajaxUrl,
              });
              if (result) {
                const lastValue = (
                  isFinderValue(lastValidValue as string)
                    ? (lastValidValue as TApiaFinderValue).storeValue
                    : lastValidValue
                ) as string;
                if (result.storeValue === lastValue) {
                  store.dispatch(
                    queryActions.setState({
                      loading: state.loading - 1 < 0 ? 0 : state.loading - 1,
                      fieldId,
                    }),
                  );
                  return 'cancel';
                }

                new ApiaFunctions().setLastModalReturn([
                  result.storeValue,
                  result.showValue,
                  ...result.fields,
                ]);
                store.dispatch(
                  queryActions.setState({
                    loading: state.loading - 1 < 0 ? 0 : state.loading - 1,
                    fieldId,
                  }),
                );
                setValue(
                  {
                    storeValue: result.storeValue,
                    showValue: result.showValue,
                  },
                  { setValueOnReadonly: true },
                );
                return true;
              }
              return false;
            }
          } else return 'cancel';
        }
        return false;
      }
      if (event === 'onChange' || event === 'JSApiaUpdate') {
        if (isReadonly && event !== 'JSApiaUpdate') return 'cancel';
        const lastValue = (
          isFinderValue(lastValidValue as string)
            ? (lastValidValue as TApiaFinderValue).storeValue
            : lastValidValue
        ) as string;
        if (typeof value !== 'string' || value === lastValue) return 'cancel';
        const { mask, regExp, maskPlaceholder } = field as IApiaInput;
        const placeHolder = maskPlaceholder || DEFAULT_MASK_PLACEHOLDER;

        if (value && regExp && !value.match(regExp)) {
          setError(
            formatMessage(
              propsStore.getFieldProps(fieldId).regExpMessage ||
                Config.MSG_INVALID_REG_EXP,
              {
                TOK1: '',
              },
            ),
          );
          return 'cancel';
        }

        const parsedValue = value.replaceAll(placeHolder, '9');
        const parsedMask = parseMask(mask);
        if (value && parsedValue === parsedMask) return 'cancel';

        let checkModalValueResult: {
          showValue: string;
          storeValue: string;
        } | null;
        try {
          checkModalValueResult = await store
            .dispatch(
              queryActions.checkModalValue({
                fieldId,
                value,
              }),
            )
            .unwrap()
            .catch(() => {
              throw new Error();
            });
        } catch (_e) {
          setError(window.MSG_VAL_NOT_FOUND);
          return 'deleteValue';
        }

        if (checkModalValueResult) {
          setValue(
            {
              storeValue: checkModalValueResult.storeValue,
              showValue: checkModalValueResult.showValue,
            },
            { setValueOnReadonly: true },
          );
          return true;
        }
        setError(Config.MSG_VAL_NOT_FOUND);
        return 'deleteValue';
      }
      return false;
    },
    preScriptEvents({ event }) {
      return event === 'JSApiaUpdate' ? 'omit' : true;
    },
    doScriptEvents: async ({ event, fireScriptEvents }) => {
      const onChangeResult = await fireScriptEvents('onChange');
      if (onChangeResult) {
        if (event === 'onModalReturn') return fireScriptEvents('onModalReturn');
        return true;
      }
      return false;
    },
    preServerEvents({ event }) {
      return event === 'JSApiaUpdate' ? 'omit' : true;
    },
    async doServerEvents({ fireServerEvents }) {
      return fireServerEvents('onChange');
    },
  },
);

export default ApiaFinder;
